/*
 * What a blacklist entry can be — shared by the one-by-one form and the
 * import (BlacklistsV2, ImportBlacklistV2), and repeated on the server
 * (BlackListService#importList). How the backend matches decides it: an
 * email is blocked when it *contains* an entry (so "@domain.com" or
 * "tempmail" work); a phone or an IP when it *starts with* one (so "+7" or
 * "203.0.113." block a range). A phone is always matched with a "+", so an
 * entry without one would never match — it is added.
 */

export type Kind = 'email' | 'phone' | 'ip'

export const KIND_LABEL: Record<Kind, string> = { email: 'Emails', phone: 'Phones', ip: 'IP addresses' }
export const KIND_ONE: Record<Kind, string> = { email: 'email', phone: 'phone', ip: 'IP address' }

/** the entry as it is stored: trimmed; a phone without spaces, dashes, brackets and dots, and with its "+" */
export function normalize(kind: Kind, v: string): string {
  const t = v.trim()
  if (kind !== 'phone') return t
  const digits = t.replace(/[\s\-().]/g, '')
  return digits.startsWith('+') ? digits : `+${digits}`
}

export const VALID: Record<Kind, (v: string) => boolean> = {
  email: (v) => v.length >= 3 && !/\s/.test(v),
  phone: (v) => /^\+\d{1,15}$/.test(v),
  ip: (v) => v.length >= 3 && v.toLowerCase() !== 'unknown' && (/^[0-9.]+$/.test(v) || /^[0-9a-f:]+$/i.test(v)),
}

export const PROBLEM: Record<Kind, string> = {
  email: 'at least 3 characters, no spaces',
  phone: 'a + and up to 15 digits',
  ip: 'digits and dots (IPv4) or hex and colons (IPv6)',
}

/** emails compare without case; phones and IPs as they are */
export const keyOf = (kind: Kind, v: string) => (kind === 'email' ? v.toLowerCase() : v)

/** a guess for one value of a mixed list; `strict` (a cell of a CSV row) wants an email to have its @ */
export function detectKind(raw: string, strict = false): Kind | null {
  const v = raw.trim()
  if (!v) return null
  if (v.includes('@')) return 'email'
  if (v.includes(':') && /^[0-9a-f:]+$/i.test(v)) return 'ip'
  if (/^[0-9.]+$/.test(v) && v.includes('.')) return 'ip'
  // a + and one digit is already a country code (+7); without the + it takes two
  if (/^\+?[\d\s\-()]+$/.test(v) && v.replace(/\D/g, '').length >= (v.startsWith('+') ? 1 : 2)) return 'phone'
  if (!strict && /[a-z]/i.test(v) && !/\s/.test(v)) return 'email'
  return null
}

const HEADER = /^(e-?mail|emails?|phones?|phone number|ip|ip ?address(es)?|value|name|comment)$/i

/** a pasted text or a .txt / .csv file as rows of cells: one value per line, or separated by , ; or tabs */
export function splitRows(text: string): string[][] {
  const rows: string[][] = []
  text.split(/\r?\n/).forEach((line) => {
    const l = line.trim()
    if (!l || l.startsWith('#')) return
    const cells = l.split(/[,;\t]/).map((c) => c.trim().replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()).filter(Boolean)
    if (cells.length) rows.push(cells)
  })
  // a header row is not data
  if (rows.length && rows[0].every((c) => HEADER.test(c))) rows.shift()
  return rows
}

/**
 * The values to load. A one-cell row is taken whole; from a row of several
 * cells (a CSV with other columns, or a list on one line) only the cells
 * that look like the target — so a name next to an email is not taken. A
 * row of several cells with none of them is handed back, to be reported.
 */
export function pickValues(rows: string[][], target: Kind | 'auto'): { values: string[]; emptyRows: string[] } {
  const values: string[] = []
  const emptyRows: string[] = []
  rows.forEach((cells) => {
    if (cells.length === 1) { values.push(cells[0]); return }
    const found = cells.filter((c) => {
      const k = detectKind(c, true)
      return !!k && (target === 'auto' || k === target)
    })
    if (found.length) values.push(...found)
    else emptyRows.push(cells.join(', '))
  })
  return { values, emptyRows }
}
